import { ErrorPanel, LinearProgress } from "@octopusdeploy/design-system-components";
import { StringHelper } from "@octopusdeploy/design-system-octopus-components";
import type { LoaderQueryState } from "@octopusdeploy/octopus-react-client";
import { APIOperationStatusProvider, useLoaderQuery } from "@octopusdeploy/octopus-react-client";
import { useTrackEvent } from "@octopusdeploy/portal-analytics";
import { semanticConventions } from "@octopusdeploy/telemetry";
import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import Dialog from "~/components/Dialog/Dialog";
import { createErrorInfoFromError } from "~/components/PageContent/createErrorInfo";
import type { UnknownPageRegistration } from "~/routing/pageRegistrations/PageRegistration";
import type { ActiveWorkflowContext } from "~/workflows/ActiveWorkflowContext";
import { ActiveWorkflowContextProvider } from "~/workflows/ActiveWorkflowContext";
import { createWorkflowAbortedEvent } from "~/workflows/analytics/WorkflowAbortedEvent";
import { createWorkflowCancelledEvent } from "~/workflows/analytics/WorkflowCancelledEvent";
import { createWorkflowCompletedEvent } from "~/workflows/analytics/WorkflowCompletedEvent";
import { renderWorkflowActions } from "~/workflows/getDrawerActionsFromWorkflowActions";
import { DrawerOutPortalProvider, useDrawerNode } from "~/workflows/useDrawerNode";
import type { CompleteWorkflow, DialogWorkflowRegistration, DrawerWorkflowRegistration, OmitWorkflowInputIfNotRequired, WorkflowRegistration } from "~/workflows/workflowRegistrations";
import { isDialogWorkflowRegistration } from "~/workflows/workflowRegistrations";
interface ActiveWorkflowProps<Input, Output, LoaderData> {
    abortSignal: AbortSignal;
    workflowRegistration: WorkflowRegistration<Input, Output, LoaderData>;
    input: Input;
    onWorkflowCompleted: CompleteWorkflow<Output>;
    workflowInstanceId: string;
    pageRegistration: UnknownPageRegistration | null;
    onWorkflowExited: (workflowInstanceId: string) => void;
}
export function ActiveWorkflow<Input, Output, LoaderData>({ workflowRegistration, input, onWorkflowCompleted, onWorkflowExited, workflowInstanceId, pageRegistration, abortSignal }: ActiveWorkflowProps<Input, Output, LoaderData>) {
    const [isActive, setIsActive] = useState(true);
    const trackEvent = useTrackEvent();
    const startTime = useRef(performance.now());
    const completeWorkflow = useCallback(
    // eslint-disable-next-line @typescript-eslint/consistent-type-assertions
    ((output: Output) => {
        if (!isActive) {
            return;
        }
        trackEvent(createWorkflowCompletedEvent({
            workflow: workflowRegistration.workflow,
            workflowInstanceId,
            pageRegistration,
            durationInMs: performance.now() - startTime.current,
        }));
        setIsActive(false);
        onWorkflowCompleted(output);
    }) as CompleteWorkflow<Output>, [isActive, onWorkflowCompleted, pageRegistration, trackEvent, workflowInstanceId, workflowRegistration.workflow]);
    const cancelWorkflow = useCallback(() => {
        if (!isActive) {
            return;
        }
        trackEvent(createWorkflowCancelledEvent({
            workflow: workflowRegistration.workflow,
            workflowInstanceId,
            pageRegistration,
            durationInMs: performance.now() - startTime.current,
        }));
        setIsActive(false);
    }, [isActive, pageRegistration, trackEvent, workflowInstanceId, workflowRegistration.workflow]);
    const abortWorkflow = useCallback(() => {
        if (!isActive) {
            return;
        }
        trackEvent(createWorkflowAbortedEvent({
            workflow: workflowRegistration.workflow,
            workflowInstanceId,
            pageRegistration,
            durationInMs: performance.now() - startTime.current,
        }));
        setIsActive(false);
    }, [isActive, pageRegistration, trackEvent, workflowInstanceId, workflowRegistration.workflow]);
    /*
    The timing of the cleanup function for the abort signal is important and should be considered
    carefully to avoid missing abort signals. As an example, if the current active state
    is checked as part of this effect, then we need to add it to the dependency array, but this
    will cause the effect to unsubscribe when the status changes, which in turn will result in the
    abort signal never firing when a consuming component completes a workflow and navigates away.
    Although it would appear to have the correct behaviour, it would be relying on the timing
    of lifecycle events which we would want to avoid.
     */
    useEffect(() => {
        //If the initial abort signal is aborted, then we need to abort the workflow, otherwise we
        //may miss the abort signal, as unlikely as this may be.
        if (abortSignal.aborted) {
            abortWorkflow();
            return;
        }
        abortSignal.addEventListener("abort", abortWorkflow);
        return () => {
            abortSignal.removeEventListener("abort", abortWorkflow);
        };
    }, [abortSignal, abortWorkflow]);
    const onExited = useCallback(() => onWorkflowExited(workflowInstanceId), [onWorkflowExited, workflowInstanceId]);
    const correlationContext = useMemo(() => ({
        "workflow.id": workflowRegistration.workflow.workflowId,
        "workflow.instance.id": workflowInstanceId,
        "workflow.owner": workflowRegistration.owner,
    }), [workflowInstanceId, workflowRegistration.owner, workflowRegistration.workflow.workflowId]);
    const queryState = useLoaderQuery<LoaderData>(async (repository) => {
        if (workflowRegistration.loader === undefined) {
            // If a workflow doesn't have a loader, it has no need for a loader.
            // Instead of trying to make the types more complex to avoid the need to pass loaderData here, it is much easier to always assume there is a loader and loader data
            // But then get consumers to ignore this value (because it will be inferred as `unknown`)
            // This is captured by the below type assertion - at runtime the value provided is simply undefined.
            // eslint-disable-next-line @typescript-eslint/consistent-type-assertions
            return undefined as unknown as LoaderData;
        }
        return await workflowRegistration.loader(repository, omitInputIfNotRequired({ input }));
    }, {
        ...correlationContext,
        [semanticConventions.operationType]: "Workflow load",
    }, `Workflow load: ${workflowRegistration.workflow.workflowId}`);
    const activeWorkflowLoadingState = useMemo<ActiveWorkflowContext>(() => ({
        isLoading: queryState.isLoading,
        loadingError: queryState.error,
        cancelWorkflow,
        hideCancelButton: workflowRegistration.hideCancelButton,
    }), [queryState.isLoading, queryState.error, cancelWorkflow, workflowRegistration.hideCancelButton]);
    return (<APIOperationStatusProvider correlationContext={correlationContext}>
            <ActiveWorkflowContextProvider value={activeWorkflowLoadingState}>
                {isDialogWorkflowRegistration(workflowRegistration) ? (<ActiveDialogWorkflow workflowRegistration={workflowRegistration} input={input} isOpen={isActive} completeWorkflow={completeWorkflow} loaderQueryState={queryState} onExited={onExited}/>) : (<ActiveDrawerWorkflow workflowRegistration={workflowRegistration} input={input} isOpen={isActive} completeWorkflow={completeWorkflow} cancelWorkflow={cancelWorkflow} loaderQueryState={queryState} onExited={onExited}/>)}
            </ActiveWorkflowContextProvider>
        </APIOperationStatusProvider>);
}
interface ActiveDialogWorkflowProps<Input, Output, LoaderData> {
    workflowRegistration: DialogWorkflowRegistration<Input, Output, LoaderData>;
    input: Input;
    isOpen: boolean;
    completeWorkflow: CompleteWorkflow<Output>;
    loaderQueryState: LoaderQueryState<LoaderData>;
    onExited: () => void;
}
function ActiveDialogWorkflow<Input, Output, LoaderData>({ workflowRegistration, input, isOpen, completeWorkflow, loaderQueryState, onExited }: ActiveDialogWorkflowProps<Input, Output, LoaderData>) {
    // We keep the children mounted so that we can transition the dialog when it is closing.
    // We then immediately unmount everything when the transition has completed.
    const keepChildrenMounted = true;
    return (<Dialog open={isOpen} onExited={onExited} keepChildrenMounted={keepChildrenMounted} dialogWidth={workflowRegistration.width}>
            {workflowRegistration.render(omitInputIfNotRequired({ input, completeWorkflow, loaderData: loaderQueryState.result }))}
        </Dialog>);
}
interface ActiveDrawerWorkflowProps<Input, Output, LoaderData> {
    workflowRegistration: DrawerWorkflowRegistration<Input, Output, LoaderData>;
    input: Input;
    isOpen: boolean;
    completeWorkflow: CompleteWorkflow<Output>;
    cancelWorkflow: () => void;
    loaderQueryState: LoaderQueryState<LoaderData>;
    onExited: () => void;
}
function ActiveDrawerWorkflow<Input, Output, LoaderData>({ workflowRegistration, input, isOpen, completeWorkflow, cancelWorkflow, loaderQueryState, onExited }: ActiveDrawerWorkflowProps<Input, Output, LoaderData>) {
    const { DrawerInPortal, DrawerOutPortal } = useDrawerNode();
    function getContent() {
        if (loaderQueryState.isLoading) {
            return (<DrawerOutPortal>
                    <LinearProgress variant={"indeterminate"} show={true}/>
                </DrawerOutPortal>);
        }
        else if (loaderQueryState.error !== null) {
            return (<DrawerOutPortal>
                    <ErrorPanel error={createErrorInfoFromError(loaderQueryState.error)}/>
                </DrawerOutPortal>);
        }
        else {
            return <DrawerOutPortalProvider drawerOutPortal={DrawerOutPortal}>{workflowRegistration.render(omitInputIfNotRequired({ input, completeWorkflow, loaderData: loaderQueryState.result }))}</DrawerOutPortalProvider>;
        }
    }
    const actions = renderWorkflowActions({
        disableCancelButton: loaderQueryState.isLoading,
        onCancelClicked: cancelWorkflow,
        primaryWorkflowAction: workflowRegistration.loadingActions?.primaryActionLabel
            ? {
                label: workflowRegistration.loadingActions.primaryActionLabel,
                disabled: true, // This will be overridden once loaded, but this will remain disable if the loader had an error
                onClick: () => { }, // This will be overridden once loaded
            }
            : undefined,
        workflowActions: (workflowRegistration.loadingActions?.actions ?? []).map((action) => ({
            label: action.label,
            disabled: true, // This will be overridden once loaded, but this will remain disable if the loader had an error
            onClick: () => { }, // This will be overridden once loaded
            buttonType: action.buttonType,
        })),
        hideCancelButton: workflowRegistration.hideCancelButton,
    });
    return (<>
            <DrawerInPortal variant="customActions" layout="none" isOpen={isOpen} onClose={cancelWorkflow} title={workflowRegistration.loadingTitle ?? StringHelper.ellipsis} customActions={actions} onExited={onExited} width={workflowRegistration.width} subtitle={workflowRegistration.subtitle} avatar={workflowRegistration.avatar?.(omitInputIfNotRequired({ input }))}>
                <></>
            </DrawerInPortal>
            {getContent()}
        </>);
}
function omitInputIfNotRequired<T extends {
    input: unknown;
}>(t: T): OmitWorkflowInputIfNotRequired<T> {
    // The function title is a bit misleading, but this function is needed to coerce the type into OmitWorkflowInputIfNotRequired
    // We don't actually remove the input property from the type because
    // - We can't because we don't have runtime information that the input type is one we want to omit (we could change this if we wanted)
    // - We don't need to. The parameter is not made available at compile time to the workflow registration, so nothing would be using it
    // eslint-disable-next-line @typescript-eslint/consistent-type-assertions
    return t as OmitWorkflowInputIfNotRequired<T>;
}
